import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
	type CSSProperties,
	type MouseEvent,
	type ReactNode,
	type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

const MENU_WIDTH = 240;
const MENU_HEIGHT_ESTIMATE = 280;
const VIEWPORT_PADDING = 8;
const GAP = 6;
const MIN_MENU_HEIGHT = 120;

export type DropdownAnchorRef = (node: HTMLButtonElement | null) => void;

export type FixedMenuCoords = {
	top: number;
	left: number;
	width?: number;
	maxHeight?: number;
	transform?: CSSProperties['transform'];
};

type GetFixedMenuCoordsOptions = {
	menuWidth?: number;
	matchAnchorWidth?: boolean;
	gap?: number;
	estimatedHeight?: number;
	align?: MenuAlign;
};

type MenuAlign = 'start' | 'center' | 'end';

type ClampMenuOptions = {
	gap?: number;
	padding?: number;
	align?: MenuAlign;
};

function computeMenuLeft(
	anchorRect: DOMRect,
	menuWidth: number,
	align: MenuAlign,
	viewportWidth: number,
	padding: number,
): number {
	let left: number;

	switch (align) {
		case 'start':
			left = anchorRect.left;
			break;
		case 'center':
			left = anchorRect.left + anchorRect.width / 2 - menuWidth / 2;
			break;
		case 'end':
		default:
			left = anchorRect.right - menuWidth;
			break;
	}

	return Math.max(
		padding,
		Math.min(left, viewportWidth - menuWidth - padding),
	);
}

export function clampMenuToViewport(
	anchor: HTMLElement,
	menuSize: { width: number; height: number },
	{
		gap = GAP,
		padding = VIEWPORT_PADDING,
		align = 'end',
	}: ClampMenuOptions = {},
): FixedMenuCoords {
	const anchorRect = anchor.getBoundingClientRect();
	const viewportWidth = window.innerWidth;
	const viewportHeight = window.innerHeight;

	const menuWidth = Math.min(menuSize.width, viewportWidth - padding * 2);
	const menuHeight = menuSize.height;

	const left = computeMenuLeft(
		anchorRect,
		menuWidth,
		align,
		viewportWidth,
		padding,
	);

	const spaceBelow = viewportHeight - anchorRect.bottom - gap - padding;
	const spaceAbove = anchorRect.top - gap - padding;
	const preferBelow = spaceBelow >= spaceAbove;

	if (preferBelow) {
		const top = Math.max(
			padding,
			Math.min(anchorRect.bottom + gap, viewportHeight - padding),
		);
		const maxHeight = Math.max(
			MIN_MENU_HEIGHT,
			Math.min(menuHeight, viewportHeight - top - padding),
		);

		return { top, left, width: menuWidth, maxHeight };
	}

	const maxHeight = Math.max(
		MIN_MENU_HEIGHT,
		Math.min(menuHeight, spaceAbove),
	);
	const top = Math.max(padding, anchorRect.top - gap - maxHeight);

	return { top, left, width: menuWidth, maxHeight };
}

export function getFixedMenuCoords(
	anchor: HTMLElement,
	{
		menuWidth = MENU_WIDTH,
		matchAnchorWidth = false,
		gap = GAP,
		estimatedHeight = MENU_HEIGHT_ESTIMATE,
		align = 'end',
	}: GetFixedMenuCoordsOptions = {},
): FixedMenuCoords {
	return clampMenuToViewport(
		anchor,
		{
			width: matchAnchorWidth
				? anchor.getBoundingClientRect().width
				: menuWidth,
			height: estimatedHeight,
		},
		{ gap, align },
	);
}

type AnchoredDropdownMenuProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	trigger: (args: {
		ref: DropdownAnchorRef;
		isOpen: boolean;
		onTriggerClick: (event: MouseEvent<HTMLButtonElement>) => void;
	}) => ReactNode;
	children: ReactNode;
	menuClassName?: string;
	menuWidth?: number;
	estimatedHeight?: number;
	matchAnchorWidth?: boolean;
	/** Quando false, o menu não recebe max-height nem overflow interno (sem scrollbar). */
	scrollable?: boolean;
	/** Alinhamento horizontal em relação à âncora de posição. */
	align?: MenuAlign;
	/** Elemento usado só para posicionar o menu (ex.: pill inteira). O trigger continua sendo o botão. */
	positionAnchorRef?: RefObject<HTMLElement | null>;
	/**
	 * Fecha ao clicar fora, mas deixa o clique seguir para o alvo (ex.: botões do Daily).
	 * Remove o overlay bloqueante e usa listener em capture sem stopPropagation.
	 */
	dismissPassthrough?: boolean;
};

const AnchoredDropdownMenu = ({
	open,
	onOpenChange,
	trigger,
	children,
	menuClassName,
	menuWidth = MENU_WIDTH,
	estimatedHeight = MENU_HEIGHT_ESTIMATE,
	matchAnchorWidth = false,
	scrollable = true,
	align = 'end',
	positionAnchorRef,
	dismissPassthrough = false,
}: AnchoredDropdownMenuProps) => {
	const anchorRef = useRef<HTMLButtonElement | null>(null);
	const menuRef = useRef<HTMLDivElement>(null);
	const [coords, setCoords] = useState<FixedMenuCoords | null>(null);

	const setAnchorRef = useCallback<DropdownAnchorRef>((node) => {
		anchorRef.current = node;
	}, []);

	const resolvePositionAnchor = useCallback((): HTMLElement | null => {
		return positionAnchorRef?.current ?? anchorRef.current;
	}, [positionAnchorRef]);

	const close = useCallback(() => {
		setCoords(null);
		onOpenChange(false);
	}, [onOpenChange]);

	const updatePosition = useCallback(() => {
		const anchor = resolvePositionAnchor();
		const menu = menuRef.current;
		if (!anchor || !menu) return;

		const measured = menu.getBoundingClientRect();
		const width = matchAnchorWidth
			? anchor.getBoundingClientRect().width
			: menuWidth;
		const height = measured.height || estimatedHeight;

		setCoords(
			clampMenuToViewport(anchor, { width, height }, { gap: GAP, align }),
		);
	}, [
		align,
		estimatedHeight,
		matchAnchorWidth,
		menuWidth,
		resolvePositionAnchor,
	]);

	const onTriggerClick = (event: MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();

		if (open) {
			close();
			return;
		}

		const anchor = resolvePositionAnchor();
		if (!anchor) return;

		setCoords(
			getFixedMenuCoords(anchor, {
				menuWidth,
				matchAnchorWidth,
				estimatedHeight,
				gap: GAP,
				align,
			}),
		);
		onOpenChange(true);
	};

	useLayoutEffect(() => {
		if (!open) {
			setCoords(null);
			return;
		}

		updatePosition();
	}, [open, updatePosition]);

	useEffect(() => {
		if (!open) return;

		const onDismissScroll = (event: Event) => {
			const target = event.target;
			if (!(target instanceof Node)) return;
			if (menuRef.current?.contains(target)) return;

			const anchor = anchorRef.current;
			if (!anchor) return;

			if (anchor.contains(target)) return;

			let parent: HTMLElement | null = anchor.parentElement;
			while (parent) {
				if (parent === target) {
					close();
					return;
				}
				parent = parent.parentElement;
			}
		};

		const onResize = () => updatePosition();

		window.addEventListener('scroll', onDismissScroll, true);
		window.addEventListener('resize', onResize);

		return () => {
			window.removeEventListener('scroll', onDismissScroll, true);
			window.removeEventListener('resize', onResize);
		};
	}, [close, open, updatePosition]);

	useEffect(() => {
		if (!open || !dismissPassthrough) return;

		const isInsideMenuOrTrigger = (target: Node) => {
			if (menuRef.current?.contains(target)) return true;
			if (anchorRef.current?.contains(target)) return true;
			return false;
		};

		const dismissIfOutside = (target: EventTarget | null) => {
			if (!(target instanceof Node)) return;
			if (isInsideMenuOrTrigger(target)) return;
			close();
		};

		const onInteractOutside = (event: Event) => {
			dismissIfOutside(event.target);
		};

		const onWindowBlur = () => {
			close();
		};

		const onFocusIn = (event: FocusEvent) => {
			dismissIfOutside(event.target);
		};

		const onScroll = (event: Event) => {
			const target = event.target;
			if (!(target instanceof Node)) return;
			if (menuRef.current?.contains(target)) return;
			close();
		};

		document.addEventListener('pointerdown', onInteractOutside, true);
		document.addEventListener('mousedown', onInteractOutside, true);
		window.addEventListener('blur', onWindowBlur);
		document.addEventListener('focusin', onFocusIn, true);
		window.addEventListener('scroll', onScroll, true);

		return () => {
			document.removeEventListener(
				'pointerdown',
				onInteractOutside,
				true,
			);
			document.removeEventListener('mousedown', onInteractOutside, true);
			window.removeEventListener('blur', onWindowBlur);
			document.removeEventListener('focusin', onFocusIn, true);
			window.removeEventListener('scroll', onScroll, true);
		};
	}, [close, dismissPassthrough, open]);

	return (
		<>
			{trigger({ ref: setAnchorRef, isOpen: open, onTriggerClick })}

			{open &&
				coords &&
				createPortal(
					<>
						{dismissPassthrough ? (
							<div
								className="pointer-events-none fixed inset-0 z-[9998]"
								aria-hidden
							/>
						) : (
							<div
								className="fixed inset-0 z-[9998]"
								aria-hidden
								onClick={(event) => {
									event.stopPropagation();
									close();
								}}
							/>
						)}
						<div
							ref={menuRef}
							role="menu"
							className={cn(
								'fixed z-[9999] rounded-2xl border border-solid border-zinc-200 bg-white shadow-xl',
								dismissPassthrough && 'pointer-events-auto',
								!coords.width && 'w-60',
								menuClassName,
							)}
							style={{
								top: coords.top,
								left: coords.left,
								width: coords.width,
								maxHeight: scrollable
									? coords.maxHeight
									: undefined,
								overflowY:
									scrollable && coords.maxHeight
										? 'auto'
										: undefined,
								transform: coords.transform,
							}}
							onClick={(event) => event.stopPropagation()}
						>
							{children}
						</div>
					</>,
					document.body,
				)}
		</>
	);
};

export { AnchoredDropdownMenu };
